/**
 * Segment Import Service
 *
 * Handles CSV file parsing, field mapping, and bulk contact import for segments.
 * Supports:
 * - CSV file upload and parsing
 * - Field mapping (CSV columns → contact fields)
 * - Validation of required fields
 * - Bulk contact creation/update
 * - Adding imported contacts to a segment
 */

import { supabase } from "./supabase";
import { logRpcError } from "./errorLog.service";
import { callWithRetry } from "../utils/transientError";
import {
  isSmsNumberBusy,
  retryOnSmsNumberBusyAround,
  SMS_NUMBER_BUSY_MESSAGE,
} from "../utils/smsNumberBusy";
import type { UUID } from "../types/common.types";
import type { Contact } from "../types/contact.types";

// Contact fields available for mapping
export const CONTACT_FIELD_OPTIONS = [
  { value: "email", label: "Email", required: true },
  { value: "first_name", label: "First Name", required: false },
  { value: "last_name", label: "Last Name", required: false },
  { value: "phone_number", label: "Phone Number", required: false },
  { value: "address_line1", label: "Address Line 1", required: false },
  { value: "address_line2", label: "Address Line 2", required: false },
  { value: "city", label: "City", required: false },
  { value: "state", label: "State", required: false },
  { value: "postal_code", label: "Postal Code", required: false },
  { value: "country", label: "Country", required: false },
  { value: "contact_type", label: "Contact Type", required: false },
  // Audit MEDIUM remediation: member_status / member_date / member_expiration_date / source / tags were
  // advertised + validated here but silently DROPPED before the bulk_import RPC (data-loss surprise).
  // Removed from the mapper so it only offers fields the import actually persists. (Persisting member
  // fields + tags is a deliberate future enhancement — tags in particular write to a separate table.)
] as const;

export type ContactFieldKey = (typeof CONTACT_FIELD_OPTIONS)[number]["value"];

export interface FieldMapping {
  csvColumn: string;
  contactField: ContactFieldKey | null;
}

export interface ImportPreviewRow {
  rowNumber: number;
  data: Record<string, string>;
  mappedData: Partial<Contact>;
  errors: string[];
  isValid: boolean;
}

export interface ImportResult {
  success: boolean;
  totalRows: number;
  // 2026-05-27 (Item H): renamed conceptually from "New Contacts" to
  // "Added to Segment". The RPC returns the count of rows added to
  // list_contacts; both new AND pre-existing contacts pass through
  // INSERT...ON CONFLICT DO UPDATE RETURNING id, then into
  // segment_rows. So importedCount = added-to-segment, not new-contacts.
  // The UI label has been changed to match.
  importedCount: number;
  // 2026-05-27 (Item H): kept on the type for backward compat but the
  // UI no longer renders this — the RPC never increments it and an
  // always-zero "Updated" counter was misleading. True created-vs-
  // existing accounting is a separate RPC enhancement.
  updatedCount: number;
  // 2026-05-27 (Item H): renamed conceptually from "Skipped" to
  // "Invalid Rows" in the UI. Only client-side validation failures
  // land here; RPC batch-level failures land in batchFailedCount.
  skippedCount: number;
  // 2026-05-27 (Item H): new counter for upload-internal duplicates.
  // Each email appears at most once after dedup; subsequent occurrences
  // are counted here and surfaced in the amber warnings panel.
  duplicateCount: number;
  // 2026-05-27 (Item H): explicit counter for RPC batch-level
  // rollbacks. After the server-side dedup migration ships this should
  // always be zero in normal-case imports. Nonzero indicates a
  // different problem (RLS, schema mismatch, etc.) and is surfaced
  // as its own stat cell only when > 0.
  batchFailedCount: number;
  errors: Array<{ row: number; error: string }>;
  // 2026-05-27 (Item H): amber-panel warnings, separate bucket from
  // red errors. Used today for upload-internal duplicate notices so a
  // successful dedup doesn't look like a partial failure.
  warnings: Array<{ row: number; warning: string }>;
  segmentId?: UUID;
  alreadyMemberCount?: number;
  newContactCount?: number;
  resolvedContactCount?: number;
}

/**
 * Normalize an email for comparison and storage. Matches:
 *  - the contacts partial unique index `lower(email) WHERE email IS NOT NULL`
 *  - the new server-side dedup in bulk_import_contacts_to_segment which
 *    keys on `lower(trim(payload->>'email'))`
 * Used at three sites in the import flow: client-side validation, the
 * in-upload dedup Set, and the RPC payload's email field.
 */
export const normalizeImportEmail = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

export interface ParsedCsvResult {
  headers: string[];
  rows: Record<string, string>[];
  totalRows: number;
}

/**
 * Parse a CSV file and return headers and rows
 */
export async function parseCsvFile(file: File): Promise<ParsedCsvResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split(/\r?\n/).filter((line) => line.trim());

        if (lines.length < 2) {
          reject(
            new Error(
              "CSV file must have at least a header row and one data row",
            ),
          );
          return;
        }

        // Parse header row
        const headers = parseCSVLine(lines[0]);

        // Parse data rows
        const rows: Record<string, string>[] = [];
        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i]);
          if (values.some((v) => v.trim())) {
            // Skip completely empty rows
            const row: Record<string, string> = {};
            headers.forEach((header, index) => {
              row[header] = values[index]?.trim() || "";
            });
            rows.push(row);
          }
        }

        resolve({
          headers,
          rows,
          totalRows: rows.length,
        });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsText(file);
  });
}

/**
 * Parse a single CSV line, handling quoted fields
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++;
      } else {
        // Toggle quote mode
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
}

/**
 * Auto-detect field mappings based on header names
 */
export function autoDetectMappings(headers: string[]): FieldMapping[] {
  const normalizeHeader = (h: string) =>
    h.toLowerCase().replace(/[^a-z0-9]/g, "");

  const mappingRules: Partial<Record<ContactFieldKey, string[]>> = {
    email: ["email", "emailaddress", "mail", "e-mail"],
    first_name: ["firstname", "first", "fname", "givenname"],
    last_name: ["lastname", "last", "lname", "surname", "familyname"],
    phone_number: [
      "phone",
      "phonenumber",
      "mobile",
      "cell",
      "telephone",
      "tel",
    ],
    // address_line2 listed before address_line1 so "address2"/"addressline2"
    // match line 2 first (line 1 patterns deliberately exclude bare "address").
    address_line2: [
      "addressline2",
      "address2",
      "apt",
      "suite",
      "unit",
      "addr2",
    ],
    address_line1: [
      "addressline1",
      "address1",
      "streetaddress",
      "street",
      "addr1",
    ],
    city: ["city", "town"],
    state: ["state", "province", "region", "st"],
    postal_code: ["postalcode", "zipcode", "zip", "postcode"],
    country: ["country", "nation"],
    contact_type: ["contacttype", "type", "category"],
  };

  return headers.map((header) => {
    const normalized = normalizeHeader(header);

    for (const [field, patterns] of Object.entries(mappingRules)) {
      if (
        patterns.includes(normalized) ||
        patterns.some((p) => normalized.includes(p))
      ) {
        return {
          csvColumn: header,
          contactField: field as ContactFieldKey,
        };
      }
    }

    return {
      csvColumn: header,
      contactField: null,
    };
  });
}

/**
 * Validate a row of data against the field mappings
 */
export function validateRow(
  row: Record<string, string>,
  mappings: FieldMapping[],
  rowNumber: number,
): ImportPreviewRow {
  const errors: string[] = [];
  const mappedData: Partial<Contact> = {};

  // Check if email is mapped
  const emailMapping = mappings.find((m) => m.contactField === "email");
  if (!emailMapping) {
    errors.push("Email field must be mapped");
  } else {
    const email = row[emailMapping.csvColumn];
    if (!email) {
      errors.push("Email is required");
    } else if (!isValidEmail(email)) {
      errors.push(`Invalid email format: ${email}`);
    } else {
      mappedData.email = email.toLowerCase().trim();
    }
  }

  // Process other mappings
  for (const mapping of mappings) {
    if (!mapping.contactField || mapping.contactField === "email") {
      continue;
    }

    const value = row[mapping.csvColumn];
    if (!value) {
      continue;
    }

    switch (mapping.contactField) {
      case "first_name":
        mappedData.first_name = value;
        break;
      case "last_name":
        mappedData.last_name = value;
        break;
      case "phone_number":
        mappedData.phone_number = normalizePhone(value);
        break;
      case "address_line1":
        mappedData.address_line1 = value;
        break;
      case "address_line2":
        mappedData.address_line2 = value;
        break;
      case "city":
        mappedData.city = value;
        break;
      case "state":
        mappedData.state = value;
        break;
      case "postal_code":
        mappedData.postal_code = value;
        break;
      case "country":
        mappedData.country = value;
        break;
      case "contact_type": {
        const validTypes = [
          "Member",
          "Expired Member",
          "Prospect",
          "Organization Contact",
        ];
        // Support comma-separated values for contact_types array
        const types = value
          .split(",")
          .map((t: string) => t.trim())
          .filter((t: string) => validTypes.includes(t));
        if (types.length > 0) {
          mappedData.contact_types = types;
          mappedData.contact_type = types[0] as Contact["contact_type"];
        } else if (validTypes.includes(value)) {
          mappedData.contact_type = value as Contact["contact_type"];
          mappedData.contact_types = [value];
        }
        break;
      }
      // member_status / member_date / member_expiration_date / source / tags intentionally NOT mapped
      // — they were dropped before the RPC (data-loss surprise). Removed from CONTACT_FIELD_OPTIONS
      // above; persisting them is a deliberate future enhancement. See migration 2026-07-02-003.
    }
  }

  return {
    rowNumber,
    data: row,
    mappedData,
    errors,
    isValid: errors.length === 0,
  };
}

/**
 * Preview import results (first N rows)
 */
export function previewImport(
  rows: Record<string, string>[],
  mappings: FieldMapping[],
  limit: number = 5,
): ImportPreviewRow[] {
  return rows
    .slice(0, limit)
    .map((row, index) => validateRow(row, mappings, index + 1));
}

interface ImportRowsIntoListOptions {
  source?: string;
  onProgress?: (processed: number, total: number) => void;
  operationName?: string;
}

function collectImportableRows(
  rows: Record<string, string>[],
  mappings: FieldMapping[],
  result: ImportResult,
): ImportPreviewRow[] {
  const validatedRows = rows.map((row, index) =>
    validateRow(row, mappings, index + 1),
  );

  const validRows = validatedRows.filter((r) => r.isValid);
  const invalidRows = validatedRows.filter((r) => !r.isValid);

  invalidRows.forEach((row) => {
    row.errors.forEach((error) => {
      result.errors.push({ row: row.rowNumber, error });
    });
  });
  result.skippedCount = invalidRows.length;

  const seenEmailKeys = new Set<string>();
  const dedupedValidRows: typeof validRows = [];
  const WARNINGS_CAP = 20;
  for (const row of validRows) {
    const emailKey = normalizeImportEmail(row.mappedData.email);
    if (seenEmailKeys.has(emailKey)) {
      result.duplicateCount += 1;
      if (result.warnings.length < WARNINGS_CAP) {
        result.warnings.push({
          row: row.rowNumber,
          warning: `Duplicate of an earlier row in this upload (${emailKey}); first occurrence kept`,
        });
      }
      continue;
    }
    seenEmailKeys.add(emailKey);
    dedupedValidRows.push(row);
  }

  return dedupedValidRows;
}

async function refreshStaticListCount(listId: UUID): Promise<number> {
  const { count, error: countError } = await supabase
    .from("list_contacts")
    .select("id", { count: "exact", head: true })
    .eq("list_id", listId);

  if (countError) {
    throw countError;
  }

  const contactCount = count || 0;
  const { error: updateError } = await supabase
    .from("lists")
    .update({
      contact_count: contactCount,
      last_calculated: new Date().toISOString(),
    })
    .eq("id", listId);

  if (updateError) {
    throw updateError;
  }

  return contactCount;
}

async function importRowsIntoList(
  dedupedValidRows: ImportPreviewRow[],
  targetListId: UUID,
  result: ImportResult,
  options: ImportRowsIntoListOptions = {},
): Promise<void> {
  const {
    source = "csv_import",
    onProgress,
    operationName = "segment_import",
  } = options;

  if (dedupedValidRows.length === 0) {
    return;
  }

  const batchSize = 250;
  let totalAlreadyMember = 0;
  let totalNewContacts = 0;
  let totalResolvedContacts = 0;
  let totalProcessedRows = 0;
  let sawResolvedKey = false;

  for (let i = 0; i < dedupedValidRows.length; i += batchSize) {
    const batch = dedupedValidRows.slice(i, i + batchSize);

    const contacts = batch.map((r) => ({
      email: normalizeImportEmail(r.mappedData.email),
      first_name: r.mappedData.first_name ?? "",
      last_name: r.mappedData.last_name ?? null,
      phone_number: r.mappedData.phone_number ?? null,
      address_line1: r.mappedData.address_line1 ?? null,
      address_line2: r.mappedData.address_line2 ?? null,
      state: r.mappedData.state ?? null,
      city: r.mappedData.city ?? null,
      postal_code: r.mappedData.postal_code ?? null,
      country: r.mappedData.country ?? null,
      contact_type:
        r.mappedData.contact_types?.[0] ?? r.mappedData.contact_type ?? null,
    }));

    // SMS plan §4.3: the chunk RPC aborts as a whole on SMS_NUMBER_BUSY (nothing written), so the
    // identical chunk is re-run by the bounded busy retry; every other error keeps the existing
    // transient-retry policy unchanged.
    const { data, error } = await retryOnSmsNumberBusyAround(
      () =>
        supabase.rpc("bulk_import_contacts_to_segment", {
          p_contacts: contacts,
          p_list_id: targetListId,
          p_source: source,
        }),
      (shielded) => callWithRetry(shielded, { retries: 2, baseDelayMs: 400 }),
    );

    if (error) {
      result.errors.push({
        row: i + 1,
        error: `Batch ${Math.floor(i / batchSize) + 1}: ${
          isSmsNumberBusy(error) ? SMS_NUMBER_BUSY_MESSAGE : error.message
        }`,
      });
      result.batchFailedCount += batch.length;
      logRpcError(operationName, "bulk_import_contacts_to_segment", error, {
        batch: Math.floor(i / batchSize) + 1,
        batch_size: batch.length,
        list_id: targetListId,
      });
    } else {
      const payload = data as {
        added?: number;
        already_member?: number;
        new_contacts?: number;
        resolved_contacts?: number;
      };
      const added = payload?.added ?? 0;
      totalAlreadyMember += payload?.already_member ?? 0;
      totalNewContacts += payload?.new_contacts ?? 0;
      if (typeof payload?.resolved_contacts === "number") {
        sawResolvedKey = true;
        totalResolvedContacts += payload.resolved_contacts;
      }
      totalProcessedRows += batch.length;
      result.importedCount += added;
    }

    onProgress?.(
      Math.min(i + batchSize, dedupedValidRows.length),
      dedupedValidRows.length,
    );
  }

  result.alreadyMemberCount = totalAlreadyMember;
  result.newContactCount = totalNewContacts;
  // resolvedContactCount is a SUCCESS signal only (never displayed). The new 4-arg RPC returns
  // resolved_contacts; PROD's old 3-arg RPC does not — so fall back to rows processed by non-error
  // batches. This keeps an idempotent all-already-members append (added=0, no resolved_contacts) from
  // false-failing if the frontend ever runs against the old RPC (deploy-order DB-first stays primary).
  result.resolvedContactCount = sawResolvedKey
    ? totalResolvedContacts
    : totalProcessedRows;
}

/**
 * Execute the import - create/update contacts and add to segment.
 *
 * Uses the bulk_import_contacts_to_segment RPC which processes an entire
 * batch server-side in one round-trip, avoiding the ~100k sequential HTTP
 * calls that caused browser timeouts on files > ~1k rows.
 */
export async function executeImport(
  rows: Record<string, string>[],
  mappings: FieldMapping[],
  segmentName: string,
  segmentDescription: string | undefined,
  userId: UUID,
  options: {
    updateExisting?: boolean;
    source?: string;
    onProgress?: (processed: number, total: number) => void;
  } = {},
): Promise<ImportResult> {
  const result: ImportResult = {
    success: false,
    totalRows: rows.length,
    importedCount: 0,
    updatedCount: 0,
    skippedCount: 0,
    duplicateCount: 0,
    batchFailedCount: 0,
    errors: [],
    warnings: [],
  };

  try {
    const dedupedValidRows = collectImportableRows(rows, mappings, result);
    if (dedupedValidRows.length === 0) {
      return result;
    }

    // Create the segment record
    const { data: segment, error: segmentError } = await supabase
      .from("lists")
      .insert({
        name: segmentName,
        description: segmentDescription,
        is_dynamic: false,
        query_criteria: { operator: "AND", conditions: [] },
        created_by: userId,
        updated_by: userId,
        contact_count: 0,
      })
      .select()
      .single();

    if (segmentError) {
      throw new Error(`Failed to create segment: ${segmentError.message}`);
    }

    result.segmentId = segment.id;

    await importRowsIntoList(dedupedValidRows, segment.id, result, {
      source: options.source,
      onProgress: options.onProgress,
      operationName: "segment_import",
    });

    // Non-fatal: the memberships are already committed server-side by the RPC. This only refreshes the
    // cached lists.contact_count display value; a transient failure here must NOT fail the import.
    try {
      await refreshStaticListCount(segment.id);
    } catch (refreshError) {
      logRpcError("segment_import", "refresh_static_list_count", refreshError, {
        list_id: segment.id,
      });
    }

    // Success ONLY when no batch failed. A partial import (some batches timed out or
    // errored) must NOT show "Import Completed Successfully" — the segment is incomplete
    // and unsafe to use until re-imported. The red error panel + batchFailedCount surface
    // the detail; this flag drives the banner/guidance.
    // Success = no batch failed AND we actually processed valid rows. Derive the "processed" signal
    // from a key present in BOTH RPC shapes: the new RPC returns resolved_contacts (which also covers
    // the legit append-all-already-members case where added=0), while PROD's older 3-arg RPC returns
    // only { added } → importedCount. This keeps the flag correct even if the frontend lands before the
    // migration (deploy-order still applies DB-first as primary), instead of falsely reporting FAILURE.
    result.success =
      result.batchFailedCount === 0 &&
      ((result.resolvedContactCount ?? 0) > 0 || result.importedCount > 0);
    return result;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    result.errors.push({ row: 0, error: errorMessage });
    return result;
  }
}

export async function appendToSegment(
  rows: Record<string, string>[],
  mappings: FieldMapping[],
  targetSegmentId: UUID,
  _userId: UUID,
  options: {
    updateExisting?: boolean;
    source?: string;
    onProgress?: (processed: number, total: number) => void;
  } = {},
): Promise<ImportResult> {
  const result: ImportResult = {
    success: false,
    totalRows: rows.length,
    importedCount: 0,
    updatedCount: 0,
    skippedCount: 0,
    duplicateCount: 0,
    batchFailedCount: 0,
    errors: [],
    warnings: [],
    segmentId: targetSegmentId,
    alreadyMemberCount: 0,
    newContactCount: 0,
    resolvedContactCount: 0,
  };

  try {
    const dedupedValidRows = collectImportableRows(rows, mappings, result);
    if (dedupedValidRows.length === 0) {
      return result;
    }

    const { data: target, error: targetError } = await supabase
      .from("lists")
      .select("id, is_dynamic")
      .eq("id", targetSegmentId)
      .maybeSingle();

    if (targetError) {
      throw targetError;
    }
    if (!target) {
      throw new Error("Segment not found");
    }
    if (target.is_dynamic === true) {
      throw new Error("Append file only applies to static segments");
    }

    await importRowsIntoList(dedupedValidRows, targetSegmentId, result, {
      source: options.source ?? "csv_append",
      onProgress: options.onProgress,
      operationName: "segment_append",
    });

    // Non-fatal: memberships are already committed by the RPC; this only refreshes the cached
    // lists.contact_count. A transient failure here must not fail an append that actually succeeded.
    try {
      await refreshStaticListCount(targetSegmentId);
    } catch (refreshError) {
      logRpcError("segment_append", "refresh_static_list_count", refreshError, {
        list_id: targetSegmentId,
      });
    }
    // Success = no batch failed AND we actually processed valid rows. Derive the "processed" signal
    // from a key present in BOTH RPC shapes: the new RPC returns resolved_contacts (which also covers
    // the legit append-all-already-members case where added=0), while PROD's older 3-arg RPC returns
    // only { added } → importedCount. This keeps the flag correct even if the frontend lands before the
    // migration (deploy-order still applies DB-first as primary), instead of falsely reporting FAILURE.
    result.success =
      result.batchFailedCount === 0 &&
      ((result.resolvedContactCount ?? 0) > 0 || result.importedCount > 0);
    return result;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    result.errors.push({ row: 0, error: errorMessage });
    return result;
  }
}

// Helper functions
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

function normalizePhone(phone: string): string {
  return phone.replace(/[^\d+]/g, "");
}
